import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import AdminShell from '../../components/admin/AdminShell';
import { adminV2Api } from '../../lib/api';
import {
  Search, Loader2, AlertCircle, ChevronLeft, ChevronRight,
  Ticket, ArrowUpRight, Calendar,
} from 'lucide-react';

const fmtINR = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;
const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};
const fmtDateTime = (ts) => {
  if (!ts) return '';
  try { return new Date(ts).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return ts; }
};

const STATUS_COLORS = {
  pending:            'bg-amber-500/15 text-amber-300 border-amber-400/40',
  awaiting_approval:  'bg-purple-500/15 text-purple-300 border-purple-400/40',
  confirmed:          'bg-emerald-500/15 text-emerald-300 border-emerald-400/40',
  cancelled:          'bg-red-500/15 text-red-300 border-red-400/40',
  refunded:           'bg-red-500/15 text-red-300 border-red-400/40',
  partially_refunded: 'bg-amber-500/15 text-amber-300 border-amber-400/40',
};

const STATUS_FILTERS = ['all', 'pending', 'awaiting_approval', 'confirmed', 'cancelled', 'refunded'];

export default function AdminBookingsPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const LIMIT = 30;

  const fetchRows = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = { limit: LIMIT, skip: (page - 1) * LIMIT };
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await adminV2Api.listBookings(params);
      setRows(res.data?.bookings || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      setError(e?.message || 'Failed to load bookings');
    } finally { setLoading(false); }
  }, [page, statusFilter]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const filtered = search.trim()
    ? rows.filter((b) =>
        (b.booking_code || '').toLowerCase().includes(search.toLowerCase()) ||
        (b.event_title || '').toLowerCase().includes(search.toLowerCase()),
      )
    : rows;
  const pages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <AdminShell title="Bookings" subtitle="All bookings across V2 events">
      <div className="admin-bookings-page" data-testid="admin-bookings-page">
        <div className="admin-events-toolbar">
          <div className="admin-events-search">
            <Search size={14} className="text-zinc-500" />
            <input
              type="text"
              placeholder="Search by code or event"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="admin-bookings-search"
            />
          </div>
          <div className="admin-events-filters">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => { setPage(1); setStatusFilter(s); }}
                className={`filter-pill ${statusFilter === s ? 'filter-pill-active' : ''}`}
                data-testid={`admin-bookings-filter-${s}`}
              >
                {s === 'all' ? 'All' : s.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="admin-events-loading"><Loader2 size={24} className="animate-spin text-[var(--accent-gold)]" /><p>Loading bookings…</p></div>
        ) : error ? (
          <div className="admin-events-error"><AlertCircle size={24} className="text-red-400 mb-2" /><p>{error}</p><button className="btn-gold mt-3" onClick={fetchRows}>Retry</button></div>
        ) : filtered.length === 0 ? (
          <div className="admin-events-empty"><Ticket size={36} className="text-[var(--accent-gold)] mb-3" /><h3 className="text-white font-semibold mb-1">No bookings found</h3><p className="text-zinc-400 text-sm">Try changing the filter or search.</p></div>
        ) : (
          <>
            <div className="admin-bookings-table" data-testid="admin-bookings-table">
              <div className="admin-bookings-row admin-bookings-row-head">
                <div>Booking</div>
                <div>Event</div>
                <div>Total</div>
                <div>Status</div>
                <div>Created</div>
                <div></div>
              </div>
              {filtered.map((b) => (
                <motion.div
                  key={b._id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.18 }}
                  className="admin-bookings-row"
                  data-testid={`admin-booking-row-${b.booking_code}`}
                >
                  <div className="admin-booking-code">
                    <span className="font-mono text-white">{b.booking_code}</span>
                    <span className="text-[10px] text-zinc-500">{(b.items || []).reduce((s, i) => s + i.quantity, 0)} pass(es)</span>
                  </div>
                  <div>
                    <div className="text-zinc-200 text-sm font-medium truncate">{b.event_title}</div>
                    <div className="text-[11px] text-zinc-500 flex items-center gap-1">
                      <Calendar size={10} /> {fmtDate(b.event_date)}
                    </div>
                  </div>
                  <div className="font-mono text-zinc-200">{fmtINR(b.total_amount)}</div>
                  <div>
                    <span className={`status-pill ${STATUS_COLORS[b.booking_status] || STATUS_COLORS.pending}`}>
                      {b.booking_status?.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  <div className="text-[11px] text-zinc-500">{fmtDateTime(b.created_at)}</div>
                  <div>
                    <Link to={`/admin/bookings/${b._id}`} className="admin-icon-btn admin-icon-btn-primary" title="View" data-testid={`admin-booking-view-${b.booking_code}`}>
                      <ArrowUpRight size={14} />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>

            {pages > 1 && (
              <div className="admin-pagination">
                <button className="bookings-page-btn" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  <ChevronLeft size={14} /> Prev
                </button>
                <span className="text-xs text-zinc-500">Page {page} of {pages}</span>
                <button className="bookings-page-btn" disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))}>
                  Next <ChevronRight size={14} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </AdminShell>
  );
}
